import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

import { InIFrameProps } from './types'

import mobileCursorUpSVG from './mobileCursorUp.svg'
import mobileCursorDownSVG from './mobileCursorDown.svg'

const EMULATE_MOBILE_CURSOR_STYLES_MOUSE_UP = `
body, body *, .p-disabled, .p-disabled * {
  cursor: url('${mobileCursorUpSVG}') 16 16, auto !important;
}`
const EMULATE_MOBILE_CURSOR_STYLE_MOUSE_DOWN = `
body, body *, .p-disabled, .p-disabled * {
  cursor: url('${mobileCursorDownSVG}') 15 15, auto !important;
}`

const STYLE_CLONES = new WeakMap<Node, { node: Node; parent: Node }>()
const registerStyleClone = (node: Node, nodeClone: Node) => {
  STYLE_CLONES.set(node, { node: nodeClone, parent: nodeClone.parentNode })
  // we need to register children too because webpack updates styles by changing text node inside style node
  const nodeChildren = node.childNodes
  const cloneNodeChildren = nodeClone.childNodes
  for (let i = 0; i < nodeChildren.length; i++) {
    const cloneNodeChild = cloneNodeChildren.item(i)
    STYLE_CLONES.set(nodeChildren.item(i), {
      node: cloneNodeChild,
      parent: cloneNodeChild.parentNode
    })
  }
}

const isStyleNode = (node: Node) =>
  Node.ELEMENT_NODE === node.nodeType &&
  ('STYLE' === node.nodeName || ('LINK' === node.nodeName && 'stylesheet' === (node as Element).getAttribute('rel')))

export default function InIFrame({
  children,
  copyStyles = true,
  syncStyles = false,
  noScrollBar = true,
  scrollContainer = false,
  seamless = true,
  emulateMobileCursor = false,
  width,
  height,
  autoHeight = false,
  maxAutoHeight,
  onAutoHeightChange,
  className,
  style,
  id
}: InIFrameProps) {
  const [iFrame, setIFrame] = useState<HTMLIFrameElement>(null)
  const iFrameDocument = iFrame?.contentWindow?.document
  const [detectedAutoHeight, setDetectedAutoHeight] = useState<number>(null)
  const initialStylesCopied = useRef<boolean>(false)
  const onAutoHeightChangeRef = useRef<(height: number) => void>(onAutoHeightChange)
  onAutoHeightChangeRef.current = onAutoHeightChange

  const scrollContainerElementRef = useRef<HTMLDivElement>(null)
  if (scrollContainer) {
    if (iFrameDocument?.body && null == scrollContainerElementRef.current) {
      scrollContainerElementRef.current = iFrameDocument.createElement('div')
      scrollContainerElementRef.current.id = 'in-iframe-scroll-container'
      scrollContainerElementRef.current.style.position = 'fixed'
      scrollContainerElementRef.current.style.overflow = 'auto'
      scrollContainerElementRef.current.style.top = '0'
      scrollContainerElementRef.current.style.left = '0'
      scrollContainerElementRef.current.style.width = width ? `${width}px` : '100%'
      iFrameDocument.body.appendChild(scrollContainerElementRef.current)
    }
  } else {
    if (iFrameDocument?.body && null != scrollContainerElementRef.current) {
      iFrameDocument.body.removeChild(scrollContainerElementRef.current)
      scrollContainerElementRef.current = null
    }
  }
  if (scrollContainerElementRef.current) {
    scrollContainerElementRef.current.style.height = `${null == detectedAutoHeight ? height : detectedAutoHeight}px`
  }

  useLayoutEffect(() => {
    let emulateCursorStyleNode: HTMLStyleElement = null
    let emulateCursorMouseDownListener: () => void = null
    let emulateCursorMouseUpListener: () => void = null
    let headStylesMutationObserver: MutationObserver = null
    let autoHeightResizeObserver: ResizeObserver = null
    let bodyStyle: HTMLStyleElement = null
    let bodyNoScrollBarStyle: HTMLStyleElement = null

    if (iFrameDocument?.head && document?.head) {
      // copy styles from parent document head to iframe document head
      if (copyStyles && !initialStylesCopied.current) {
        document.head.querySelectorAll('style, link[rel="stylesheet"]').forEach(styleNode => {
          const styleCloneNode = styleNode.cloneNode(true)
          iFrameDocument.head.appendChild(styleCloneNode)
          registerStyleClone(styleNode, styleCloneNode)
        })
        initialStylesCopied.current = true
      }

      // sync style changes from parent document head to iframe document head
      if (syncStyles) {
        headStylesMutationObserver = new MutationObserver(records => {
          try {
            for (const record of records) {
              // add new nodes to iframe document head
              record.addedNodes.forEach(node => {
                if (STYLE_CLONES.has(node.parentNode)) {
                  // if parent are registered, clone node and append to parent (webpack update styles by changing text node inside style node)
                  const { node: parentNode } = STYLE_CLONES.get(node.parentNode)
                  const clonedNode = node.cloneNode(true)
                  parentNode.appendChild(clonedNode)
                  registerStyleClone(node, clonedNode)
                } else if (iFrameDocument?.head && isStyleNode(node)) {
                  // else if node is style node, clone node and append to iframe document head
                  const styleCloneNode = node.cloneNode(true)
                  iFrameDocument.head.appendChild(styleCloneNode)
                  registerStyleClone(node, styleCloneNode)
                }
              })
              // remove registered nodes from iframe document head
              record.removedNodes.forEach(node => {
                if (STYLE_CLONES.has(node)) {
                  const { node: styleCloneNode, parent } = STYLE_CLONES.get(node)
                  parent.removeChild(styleCloneNode)
                  STYLE_CLONES.delete(node)
                }
              })
            }
          } catch {
            // ignore changes if something went wrong for the safety reasons
          }
        })
        headStylesMutationObserver.observe(document.head, {
          childList: true,
          subtree: true
        })
      }

      // add body style
      bodyStyle = iFrameDocument.createElement('style')
      bodyStyle.textContent = `
        body, #in-iframe-scroll-container {
          min-width: 100% !important;
          ${autoHeight ? 'height: -webkit-fit-content !important; height: fit-content !important;' : ''};
          margin: 0;
          padding: 0;
        }`
      iFrameDocument.head.appendChild(bodyStyle)

      // add styles to hide scrollbars
      if (noScrollBar) {
        bodyNoScrollBarStyle = iFrameDocument.createElement('style')
        bodyNoScrollBarStyle.textContent = `
          body::-webkit-scrollbar, body::-webkit-scrollbar-button, body::-webkit-scrollbar-thumb, body::-webkit-scrollbar-track, body::-webkit-scrollbar-track-piece,
          #in-iframe-scroll-container::-webkit-scrollbar, #in-iframe-scroll-container::-webkit-scrollbar-button, #in-iframe-scroll-container::-webkit-scrollbar-thumb, #in-iframe-scroll-container::-webkit-scrollbar-track, #in-iframe-scroll-container::-webkit-scrollbar-track-piece {
            width: 0px;
            background: transparent;
          }
          body::-moz-scrollbar,
          #in-iframe-scroll-container::-moz-scrollbar {
            display: none;
            scrollbar-color: transparent transparent;
          }`
        iFrameDocument.head.appendChild(bodyNoScrollBarStyle)
      }

      // add styles to emulate mobile cursor and register mouse down/up listeners to change cursor image
      if (emulateMobileCursor) {
        try {
          // preload cursor images
          const mobileCursorUpImage = iFrameDocument.createElement('img')
          mobileCursorUpImage.src = mobileCursorUpSVG
          const mobileCursorDownImage = iFrameDocument.createElement('img')
          mobileCursorDownImage.src = mobileCursorDownSVG
        } catch {
          //
        }
        emulateCursorStyleNode = iFrameDocument.createElement('style')
        emulateCursorStyleNode.textContent = EMULATE_MOBILE_CURSOR_STYLES_MOUSE_UP
        iFrameDocument.head.appendChild(emulateCursorStyleNode)
        iFrameDocument.addEventListener(
          'mousedown',
          (emulateCursorMouseDownListener = () => {
            emulateCursorStyleNode.textContent = EMULATE_MOBILE_CURSOR_STYLE_MOUSE_DOWN
          }),
          true
        )
        iFrameDocument.addEventListener(
          'mouseup',
          (emulateCursorMouseUpListener = () => {
            emulateCursorStyleNode.textContent = EMULATE_MOBILE_CURSOR_STYLES_MOUSE_UP
          }),
          true
        )
      }

      // manage auto height
      if (autoHeight) {
        let updateDebounceTimer: ReturnType<typeof setTimeout> = null
        const updateAutoHeight = () => {
          if (updateDebounceTimer) {
            clearTimeout(updateDebounceTimer)
          }
          updateDebounceTimer = setTimeout(() => {
            updateDebounceTimer = null
            const updatedHeight = (
              null == scrollContainerElementRef.current ? iFrameDocument.body : scrollContainerElementRef.current
            ).offsetHeight
            setDetectedAutoHeight(updatedHeight)
          }, 200)
        }
        updateAutoHeight()
        autoHeightResizeObserver = new ResizeObserver(updateAutoHeight)
        autoHeightResizeObserver.observe(
          null == scrollContainerElementRef.current ? iFrameDocument.body : scrollContainerElementRef.current
        )
      }
    }

    // cleanup
    return () => {
      if (iFrameDocument && emulateCursorMouseDownListener) {
        iFrameDocument.removeEventListener('mousedown', emulateCursorMouseDownListener, true)
      }
      if (iFrameDocument && emulateCursorMouseUpListener) {
        iFrameDocument.removeEventListener('mouseup', emulateCursorMouseUpListener, true)
      }
      if (iFrameDocument && bodyNoScrollBarStyle) {
        iFrameDocument.head.removeChild(bodyNoScrollBarStyle)
        bodyNoScrollBarStyle = null
      }
      if (iFrameDocument && bodyStyle) {
        iFrameDocument.head.removeChild(bodyStyle)
        bodyStyle = null
      }
      if (headStylesMutationObserver) {
        headStylesMutationObserver.disconnect()
        headStylesMutationObserver = null
      }
      if (autoHeight && autoHeightResizeObserver) {
        autoHeightResizeObserver.disconnect()
        setDetectedAutoHeight(null)
        autoHeightResizeObserver = null
      }
    }
  }, [iFrameDocument, copyStyles, syncStyles, noScrollBar, emulateMobileCursor, autoHeight])

  useEffect(() => {
    if (null !== detectedAutoHeight && onAutoHeightChangeRef.current && iFrame) {
      const iFrameHeight = iFrame.offsetHeight
      onAutoHeightChangeRef.current(iFrameHeight)
    }
  }, [iFrame, detectedAutoHeight])

  return (
    <>
      <iframe
        // ref={iframe => {
        //   if (null == iFrameDocument && iframe) {
        //     setIFrame(iframe)
        //   }
        // }}
        onLoad={event => {
          const iframe = event.target as HTMLIFrameElement
          if (null == iFrameDocument && iframe) {
            setIFrame(iframe)
          }
        }}
        srcDoc="<!DOCTYPE html>"
        width={width}
        height={null == detectedAutoHeight ? height : detectedAutoHeight}
        className={className}
        style={null == maxAutoHeight ? style : { maxHeight: maxAutoHeight, ...style }}
        id={id}
        seamless={seamless}
      />
      {iFrameDocument
        ? createPortal(
            children,
            null == scrollContainerElementRef.current ? iFrameDocument.body : scrollContainerElementRef.current
          )
        : null}
    </>
  )
}
